/**
 * Sometimes, figma-exported .png file is imported as BLOB rather than a resolved image path.
 * In that case, use export image as an svg.
 */
import commentsWidgetImage from "./img/comments_widget.jpg";
import checkGreenRound from "./img/check-green-round.png";
import cards from "./img/cards.png";
import cvvIcon from "./img/cvv-icon.png";
import guarantee from "./img/guarantee.png";
import greenLock from "./img/green-lock.svg";
import quizImage from "./img/multi-step-quiz.png";
import quizImageV3 from "./img/multi-step-quiz-v3.svg";
import quizImageV4 from "./img/multi-step-quiz-v4.svg";
import toastImage from "./img/toast-img.png";
import livechatImage from "./img/live-chat-img.svg";
import audioFormImage from "./img/audio-form-img.png";
import fourStars from "./img/stars_4_and_half.svg";
import editor from "./img/avatars/editor.svg";
import developer from "./img/avatars/developer.svg";
import association_member from "./img/avatars/association_member.svg";
import analyst from "./img/avatars/analyst.svg";
import bottomBarImage from "./img/bottom-bar.png";
import zipCodeImage from "./img/zip-code-img.svg";

import { hexToRgb, rgbToHex } from "@/@core/utils";
import { calculateTimeRemaining } from "@/utility/calculateTimeRemaining";

const IMAGES_API = process.env.VUE_APP_API;

const getGifAgeTemplate = options => {
  return `
    <div>
      <a
        href="${options.url}"
        target="${options.tab}"
      >
        <img class="mx-auto" src="${IMAGES_API}/images/misc/age-gif.gif" alt=""
        data-image>
      </a>
    </div>
  `;
};

const getCTATemplateWidthAd = options => {
  return `
    <a
      data-shortcode='buttons'
      class="button-width-ad flex justify-center align-center w-full bg-gradient-to-b from-green-6 to-green-7 text-22 leading-26 font-bold text-white px-16 py-6 md:py-18px rounded-xl border-b-5 border-green-8 relative"
      href="${options.url}"
      target="${options.tab}"
      >
        <span class="text-center">
           ${options.text}
           <img
            class="ml-4 inline"
            src="${IMAGES_API}/images/misc/arrow.svg"
            data-image
            alt="Arrow"
          >
        </span>

        <span class="absolute text-13 bottom-0 right-2 leading-none">
            Ad
        </span>
    </a>
  `;
};

const getCTATemplate = options => {
  return `
    <a
      style="background: ${options?.bgColor};"
      data-shortcode='buttons'
      class="button button--green"
      href="${options?.url}"
      target="${options?.tab}"
      >
      <div class="">
        <span>${options?.text}</span>
        <img
          class="inline ml-2"
          src="${IMAGES_API}/images/misc/arrow.svg"
          data-image
          alt="Arrow"
        >
      </div>
    </a>
  `;
};

const getSelectAgeTemplate = options => {
  const ages = ["Under 30", "30-39", "40-49", "50-59", "60-69", "Over 70"];
  let template = "<div class='mx-auto max-w-3.5xl'><div class='select-age__button-container'>";

  for (const age of ages) {
    template += `
      <a
        data-shortcode="buttons"
        href="${options?.url}"
        target="${options?.tab}"
        class="select-age__button--green ${options?.type}-bg"
      >
        ${age}
      </a>
    `;
  }
  template += "</div></div>";

  return template;
};

const getSelectAgeIconsTemplate = options => {
  const ages = ["<40", "40 - 49", "50 - 59", "60 - 69", "70+"];
  const genders = ["m", "f"];
  let template = '<div class="max-w-5xl inline-flex flex-wrap justify-center md:justify-normal">';
  genders.forEach(gender => {
    ages.forEach((age, index) => {
      template += `
      <a
      href="${options?.url}"
      target="${options?.tab}"
      class="age-icons__item ${options?.color}-bg"
      >
      <img
          src='${IMAGES_API}/images/peoples/age-avatars/${gender}-${index + 1}.svg')
          data-image alt="avatar image" />
          ${age}
        </a>
      `;
    });
  });
  template += "</div>";

  return template;
};

const getMileageTemplate = options => {
  const miles = ["30 or less", "31 - 59 miles", "60+ miles"];

  let template = '<div data-shortcode="buttons" class="flex -mx-3">';

  for (const mile of miles) {
    template += `
    <div class="px-3 w-full">
      <div class="button w-full font-bold flex h-full items-center text-22 md:text-24 justify-center leading-snug">
        <a
          href="${options?.url}"
          target="${options?.tab}"
          class="button w-full font-bold flex h-full items-center text-22 md:text-24 justify-center button--green ${options?.color}-bg"
        >
          ${mile}
      </a>
      </div>
    </div>
    `;
  }
  template += "</div>";

  return template;
};

const getCtaButtonNumberTemplate = options => {
  let targetAttribute = "";
  if (!options?.hrefType) {
    targetAttribute = `target="${options?.tab}"`;
  }

  return `
    <a
      data-shortcode='buttons'
      style="background-color: ${options?.bgColor}; border-color: ${options?.borderColor};"
      class="mx-auto flex max-w-45 border-2 rounded-lg shadow-gray-bottom-light px-10 md:px-16 py-5 md:py-6 items-center font-roboto no-underline justify-center button-animation-pulsing text-white"
      href="${options?.hrefType}${options?.url}"
      ${targetAttribute}
    >
        <div class="mr-6 md:mr-35 w-30px md:w-auto">
            <img data-image src="${IMAGES_API}/images/phone.svg" alt="phone icon">
        </div>
        <div>
            <div class="text-white text-22 md:text-36 leading-30 md:leading-40 font-extrabold">${options?.number}</div>
            <div class="text-white text-14 md:text-16 leading-20 md:leading-24 font-semibold">${options?.text}</div>
        </div>
    </a>
  `;
};

const getCTAPhoneTemplate = options => {
  return `
    <a
      data-shortcode='buttons'
      style="background: ${options?.bgColor};"
      class="button button--green"
      href="tel:${options?.number}"
      >
      <div class="flex justify-center">
        <span>${options?.text}</span>
        <img
          class="inline ml-2 w-22px"
          src="${IMAGES_API}/images/phone.svg"
          data-image
          alt="phone icon"
        >
      </div>
    </a>
  `;
};

const getCTAIndyTemplate = options => {
  return `
       <a
      data-shortcode='buttons'
      class="button-indy button-indy--${options.type}"
      href="${options.url}"
      target="${options.tab}"
      >
           <span>${options.text}</span>
           <img
               class="ml-4 md:ml-8"
               src="${IMAGES_API}/images/indy_arrow.svg"
               data-image
               alt="Arrow"
           >
    </a>
  `;
};

const getShortcodeHTMLTemplate = content => {
  const template = `
    <div>
      <div class="advert--title">ADVERT</div>
      <div class="custom-partner" data-tiny-mce-component="custom-partner">
        ${content?.html}
      </div>
    </div>
    `;

  return template;
};

const getShortCTATemplate = options => {
  return `
    <a
      data-shortcode='buttons'
      style="background: ${options?.bgColor};"
      class="button button--short button--short--green"
      href="${options?.url}"
      target="${options?.tab}"
      >
           <span>${options?.text}</span>
    </a>
    `;
};

const getLiveChatCTATemplate = options => {
  return `
    <a
      data-shortcode='buttons'
      style="background: ${options?.bgColor};"
      class="button--livechat ${options?.pulsingAnimation ? "animated-button" : ""} ${
    options?.spaceBellow ? "mb-16px" : ""
  }"
      href="${options?.url}"
      >
           <span>${options?.text}</span>
    </a>
    `;
};

const getCTAOuterTemplate = options => {
  return `
       <div class="md:flex" >
           <div class="md:mr-12">
               <a
               class="button-outer"
               href="${options.url}"
               target="${options.tab}"
               >
                   <span>${options.text}</span>
                   <img
                       class="ml-4 md:ml-8"
                       src="${IMAGES_API}/images/indy_arrow.svg"
                       data-image
                       alt="Arrow"
                   >
               </a>
           </div>
           <div class="hidden md:flex items-center text-18 font-semibold leading-30 ">
                Press Button
                <img src="${IMAGES_API}/images/arrow-left.svg" data-image class="ml-4" alt="arrow">
           </div>
       </div>
  `;
};

const getMultipleCTATemplate = options => {
  if (options?.form === "squared") {
    return `
  <div>
    <div class="mt-4 mb-8">
      <a
        style="background: ${options?.bgColor}; border-color: ${options?.borderColor};"
        data-shortcode='buttons'
        class="button button--yes-no button--green"
        href="${options?.url}"
        target="${options?.tab}"
        >
            <span>YES</span>
      </a>
    </div>
    <div class="mb-4 mt-8">
      <a
        style="background: ${options?.bgColor}; border-color: ${options?.borderColor};"
        data-shortcode='buttons'
        class="button button--yes-no button--green"
        href="${options?.url}"
        target="${options?.tab}"
      >
          <span>NO</span>
      </a>
    </div>
  </div>
  `;
  } else {
    return `
  <div>
    <div class="mt-4 mb-6 md:mb-8">
      <a
        style="background: ${options?.bgColor}; border-color: ${options?.borderColor};"
        data-shortcode='buttons'
        class="buttons-rounded buttons-rounded--green"
        href="${options?.url}"
        target="${options?.tab}"
        >
            <span>YES</span>
      </a>
    </div>
    <div class="mt-4 mb-6 md:mb-8">
      <a
        style="background: ${options?.bgColor}; border-color: ${options?.borderColor};"
        data-shortcode='buttons'
        class="buttons-rounded buttons-rounded--green"
        href="${options?.url}"
        target="${options?.tab}"
      >
          <span>NO</span>
      </a>
    </div>
  </div>
  `;
  }
};

const getCheckoutPlanTemplate = () => {
  return `
    <div data-shortcode='checkoutplan' class="checkout-plan">
      <div class="border-4 border-green-5 rounded-xl px-6 py-8 flex items-baseline">
        <div>
            <div class="w-22px">
                <img src="${checkGreenRound}" alt="">
              </div>
            </div>
            <div class="ml-8">
                <div class="text-26 leading-38 md:text-33 md:leading-46 text-black font-bold font-oswald">
                Lifetime Access To Dollar Save - $39
                 </div>
                 <div class="bg-yellow-1 text-16 leading-28 font-medium px-4 font-oswald mt-6 md:w-max pb-1">
                                BEST DEAL: Save 80% -- One-time payment of $39
                </div>
                <div class="mt-6 ml-8">
                    <ul class="font-semibold text-black list-disc checkout-plan__list font-open">
                        <li>
                            Money for a Lifetime: 7 Simple Steps to Getting the Most from Social Security ($49 Value) - FREE
                         </li>
                       </ul>
                 </div>
               </div>
           </div>
    </div>
  `;
};

const getQuoteBoxTemplate = () => {
  return `
    <div data-shortcode='quoteratings' class="quote-rating">
      <div class="quote-rating__wrapper">
        <div class="quote-rating__quote">
          Quote
        </div>
        <div class="quote-rating__author">
          Author
          <div>
            <img src="${IMAGES_API}/images/icons/stars_5.svg" alt="Star icons">
            </div>
          </div>
        </div>
      </div>
  `;
};

const getQuoteTemplate = () => {
  return `
    <div data-shortcode='quote' class="quote">
      Quote
    </div>
  `;
};

const getWhiteMultiButtonCta = options => {
  const defaultTextItems = {
    "#E01107": "Unsold Luxury SUV Pricing",
    "#2B9F11": "Clearance Pricing On Unsold SUVs",
    "#F87012": "Unsold Luxury SUV Prices (You May Be Surprised)",
    "#0561D8": "Midsize Crossover SUVs Prices Now",
  };
  if (options?.quantity === "5") {
    defaultTextItems["#A7BB3C"] = "Free Bank Accounts That Offer Large Cash Bonuses";
  }
  let template = '<div class="flex flex-col md:flex-row md:flex-wrap mx-auto justify-between">';

  for (const [key, value] of Object.entries(defaultTextItems)) {
    template += `
      <div class="multi-button-cta-white block bg-gray-12 rounded-15px shadow-f-solid text-18 font-bold leading-24 border-2 border-white flex justify-between items-center min-h-60px">
        <a
          data-shortcode='buttons'
          href="${options?.url}"
          target="${options?.tab}"
          class="w-full py-4px pl-8 pr-69px"
        >
          <span>${value}</span>
        </a>
        <svg width="48" height="48" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M29.9102 0.341309C45.9303 0.341309 58.9102 13.3211 58.9102 29.3413C58.9102 45.3615 45.9303 58.3413 29.9102 58.3413C13.89 58.3413 0.910156 45.3615 0.910156 29.3413C0.910156 13.3211 13.89 0.341309 29.9102 0.341309ZM43.2291 27.3534L27.3844 11.5086C26.2852 10.4095 24.5077 10.4095 23.4202 11.5086L21.4323 13.4966C20.3331 14.5957 20.3331 16.3732 21.4323 17.4607L33.313 29.3413L21.4323 41.222C20.3331 42.3212 20.3331 44.0986 21.4323 45.1861L23.4202 47.174C24.5194 48.2732 26.2969 48.2732 27.3844 47.174L43.2291 31.3292C44.3283 30.23 44.3283 28.4526 43.2291 27.3534Z" fill="${key}"/>
        </svg>
     </div>
    `;
  }

  template += "</div>";

  return template;
};

const getMultiButtonCta = options => {
  const defaultTextItmes = [
    "Unsold Luxury SUV Senior Pricing",
    "Unsold  SUV Prices (You Be Surprised)",
    "2020 Jeep Grand Cherokee Offers",
    "Midsize Crossover SUVs Prices Now",
  ];
  if (options?.quantity === "5") {
    defaultTextItmes.push("Free Bank Accounts That Offer Large Cash Bonuses");
  }
  let template = '<div class="flex flex-col md:flex-row md:flex-wrap mx-auto justify-between">';

  for (const item of defaultTextItmes) {
    template += `
      <div class="multi-button-cta-item block bg-gradient-to-b from-blue-401 via-blue-402 to-blue-403 rounded-20px shadow-b-r-heavy text-18 font-bold leading-24 border-2 border-blue-404 flex text-white justify-between min-h-60px">
        <a
          data-shortcode='buttons'
          class="w-full buttons"
          href="${options?.data?.url}"
          target="${options?.data?.tab}"
        >
          <span>${item}</span>
        </a>
        <img class="" src="${IMAGES_API}/images/arrow-right.svg" data-image alt="arrow right">
     </div>
    `;
  }

  template += "</div>";

  return template;
};

const getMultiButtonCtaWithSeparateLinks = options => {
  let template = '<div class="flex flex-col md:flex-row md:flex-wrap mx-auto justify-between">';
  for (let x = 1; x <= options?.quantity; x++) {
    template += `
      <div class="multi-button-cta-item block bg-gradient-to-b from-blue-401 via-blue-402 to-blue-403 rounded-20px shadow-b-r-heavy text-18 font-bold leading-24 border-2 border-blue-404 flex text-white justify-between min-h-60px">
        <a
          data-shortcode='buttons'
          class="py-4px pl-9 pr-16 w-full buttons flex items-center"
          href="${options?.data["customUrl" + x]}"
          target="${options?.data["openLink" + x]}"
        >
          <span>${options?.data["buttonInput" + x]}</span>
        </a>
        <img class="" src="${IMAGES_API}/images/arrow-right.svg" data-image alt="arrow right">
     </div>
    `;
  }
  template += "</div>";

  return template;
};

const getMultiButtonCtaV3Template = options => {
  let template = '<div class="multi-button-cta-V3">';
  if (options?.count) {
    for (let i = 1; i <= options?.count; i++) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="multi-button-cta-V3__button"
        style="background-color: ${options?.bgColor};"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'"
      >
        Text - ${i}
      </a>
    `;
    }
  }

  template += "</div>";

  return template;
};

const getMultiButtonCtaV4Template = options => {
  const ctaOptions = [
    {
      title: "Healthy Groceries",
      image: `${IMAGES_API}/images/cta-icon-1.svg`,
      alt: "groceries",
    },
    {
      title: "Utilities",
      image: `${IMAGES_API}/images/cta-icon-2.svg`,
      alt: "utilities",
    },
    {
      title: "OTC & Prescription",
      image: `${IMAGES_API}/images/cta-icon-3.svg`,
      alt: "medicine",
    },
    {
      title: "Transportation",
      image: `${IMAGES_API}/images/cta-icon-4.svg`,
      alt: "car",
    },
    {
      title: "Dental",
      image: `${IMAGES_API}/images/cta-icon-5.svg`,
      alt: "tooth",
    },
    {
      title: "Vision & Hearing",
      image: `${IMAGES_API}/images/cta-icon-6.svg`,
      alt: "ear",
    },
  ];

  let template = '<div class="multi-button-cta-V4">';
  for (const cta of ctaOptions) {
    template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="multi-button-cta-V4__button button-animation-pulsing-1025"
        style="background-color: ${options?.bgOptionColor};"
      >
      <span>${cta.title}</span>
      <img src="${cta.image}" alt="${cta.alt}" data-image>
      </a>
    `;
  }

  template += "</div>";
  template += `
    <a
      href="${options?.url}"
      target="${options?.tab}"
      class="multi-button-cta-V4__all-button button-animation-pulsing-1025"
      style="background-color: ${options?.bgMainColor};"
    >
    <span>All of the Above
    <img class="mobile-img" src="${IMAGES_API}/images/checkmark-red.svg" alt="checkmark" data-image></span>
    </a>
  `;

  return template;
};

const getDynamicColorMultiButtonCta = options => {
  const iconsColors = ["#E01107", "#9E12AA", "#DE9258", "#265C9B"];
  if (options?.styles?.quantity === "5") {
    iconsColors.push("#A7BB3C");
  }

  let template = '<div class="flex flex-col md:flex-row md:flex-wrap mx-auto justify-between max-w-76">';
  let count = 1;

  for (const key of iconsColors) {
    template += `
      <div
        onMouseOver="this.style.backgroundColor='${options?.styles?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.styles?.bgColor}'"
        style="background-color: ${options?.styles?.bgColor}"
        class="multi-button-cta-dynamic block rounded-15px shadow-f-solid text-18 font-bold leading-24 flex justify-between items-center min-h-60px">
        <a
          data-shortcode='buttons'
          href="${options?.data?.["customUrl" + count]}"
          target="${options?.data?.["openLink" + count]}"
          class="w-full py-4px pl-6 pr-69px flex items-center"
        >
          <span class="mr-4 text-22">${count}</span>
          <span>${options?.data?.["buttonInput" + count]}</span>
        </a>
        <svg width="46" height="46" viewBox="0 0 59 59" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M29.9102 0.341309C45.9303 0.341309 58.9102 13.3211 58.9102 29.3413C58.9102 45.3615 45.9303 58.3413 29.9102 58.3413C13.89 58.3413 0.910156 45.3615 0.910156 29.3413C0.910156 13.3211 13.89 0.341309 29.9102 0.341309ZM43.2291 27.3534L27.3844 11.5086C26.2852 10.4095 24.5077 10.4095 23.4202 11.5086L21.4323 13.4966C20.3331 14.5957 20.3331 16.3732 21.4323 17.4607L33.313 29.3413L21.4323 41.222C20.3331 42.3212 20.3331 44.0986 21.4323 45.1861L23.4202 47.174C24.5194 48.2732 26.2969 48.2732 27.3844 47.174L43.2291 31.3292C44.3283 30.23 44.3283 28.4526 43.2291 27.3534Z" fill="${key}"/>
        </svg>
     </div>
    `;
    count++;
  }

  template += "</div>";

  return template;
};

const getMoneyCTATemplate = () => {
  const moneyOptions = [
    {
      title: "$0 - $15K",
      image: `${IMAGES_API}/images/money-1.svg`,
      alt: "money-1",
    },
    {
      title: "$16K - $25K",
      image: `${IMAGES_API}/images/money-2.svg`,
      alt: "money-2",
    },
    {
      title: "$26K - $50K",
      image: `${IMAGES_API}/images/money-3.svg`,
      alt: "money-3",
    },
    {
      title: "$51K - $100K",
      image: `${IMAGES_API}/images/money-4.svg`,
      alt: "money-4",
    },
    {
      title: "$100K+",
      image: `${IMAGES_API}/images/money-5.svg`,
      alt: "money-5",
    },
  ];

  let template = '<div class="money-cta-buttons-wrapper">';

  for (const money of moneyOptions) {
    template += `
        <a
          class="money-cta-button"
          href="#"
        >
          <img src="${money.image}" alt="${money.alt}" data-image>
          <span>${money.title}</span>
        </a>
    `;
  }

  template += "</div>";

  return template;
};

const getButtonsWithTextBoxTemplate = (data, buttonArray, boxArray) => {
  if (!data) {
    return "buttons-with-text-box";
  }
  let template = '<div class="buttons-with-text-box"><div class="wrapper">';

  data.buttons.forEach((button, index) => {
    if (button.type === "url") {
      template += `
        <a
          href="${button.url}"
          target="${button.tab}"
          class="button-with-text-box-link"
          style="background-color: ${data.primaryColor};"
        >
          ${buttonArray?.[index] ? buttonArray[index] : `<span>${button.text}</span>`}
        </a>
      `;
    } else {
      template += `
        <div
          class="button-with-text-box-link"
          style="background-color: ${data.primaryColor};"
        >
          ${buttonArray?.[index] ? buttonArray[index] : `<span>${button.text}</span>`}
        </div>`;
    }
  });

  template += "</div>";

  data.buttons.forEach((button, index) => {
    if (button.type === "textBox") {
      template += `
        <div class="button-with-text-box-text" data-button-index="${index}" style="border-color: ${
        button.textBorderColor
      }; background-color: ${button.textBgColor}">
          ${boxArray?.[index] ? boxArray[index] : `<span>${button.text}</span>`}
        </div>
      `;
    }
  });

  template += "</div>";

  return template;
};

const getButtonsWithTextBoxVueTemplate = (data, buttonsArray, boxArray) => {
  return `<buttons-with-text-box data="${data}" buttons-array="${buttonsArray}" box-array="${boxArray}"></buttonsWithTextBox>`;
};

const getSelectAgeInOneRowTemplate = options => {
  const fourButtonsAges = ["Under 50", "50-70", "71-90", "Over 90"];
  const threeButtonsAges = ["Under 65", "65-80", "Over 80"];
  let template = '<div class="select-one-row">';
  if (options?.count === "3") {
    for (const age of threeButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count}"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor};"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  } else if (options?.count === "4") {
    for (const age of fourButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count}"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor};"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  }

  template += "</div>";

  return template;
};
const getAgeCTA3Template = options => {
  let shadowStyle = "";
  if (options) {
    const backgroundRGB = hexToRgb(options?.bgColor);
    const shadowColorThreshold = 80;
    const shadowRGB = {};
    const shadowR = backgroundRGB.r - shadowColorThreshold;
    const shadowG = backgroundRGB.g - shadowColorThreshold;
    const shadowB = backgroundRGB.b - shadowColorThreshold;

    shadowRGB.r = shadowR < 0 ? 0 : shadowR;
    shadowRGB.g = shadowG < 0 ? 0 : shadowG;
    shadowRGB.b = shadowB < 0 ? 0 : shadowB;
    const shadowHex = rgbToHex(shadowRGB.r, shadowRGB.g, shadowRGB.b);
    shadowStyle = `box-shadow: 0px 4px 4px 0px ${shadowHex};`;
  }
  const fourButtonsAges = ["Under 50", "50-70", "71-90", "Over 90"];
  const threeButtonsAges = ["Under 65", "65-80", "Over 80"];
  let template = '<div class="select-one-row age-cta-3">';
  if (options?.count === "3") {
    for (const age of threeButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count}"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor}; ${
        shadowStyle ? shadowStyle : ""
      } border-radius: ${options?.cornerRadius}px;"
      >
        ${age}
      </a>
    `;
    }
  } else if (options?.count === "4") {
    for (const age of fourButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count}"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor}; ${
        shadowStyle ? shadowStyle : ""
      } border-radius: ${options?.cornerRadius}px;"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${
        options?.hoverColor
      }'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  }

  template += "</div>";

  return template;
};

const getAgeCTA4Template = options => {
  if (!options) {
    return "age-cta-4";
  }

  const titles = {
    1: ["Age: <40", "Age: 40-49", "Age: 50-59", "Age: 60-69", "Age: 70+"],
    2: {
      desktop: ["<40", "40-49", "50-59", "60-69", "70+"],
      mobile: ["Age: <40", "Age: 40-49", "Age: 50-59", "Age: 60-69", "Age: 70+"],
    },
    3: ["<40", "40-49", "50-59", "60-69", "70+", "<40", "40-49", "50-59", "60-69", "70+"],
    4: ["<40", "40-49", "50-59", "60-69", "70+", "<40", "40-49", "50-59", "60-69", "70+"],
  };
  const images = [];

  if (options.version == 1 || options.version == 2) {
    for (let i = 1; i <= 5; i++) {
      if (options.version == 2) {
        images.push({
          src: `${IMAGES_API}/images/portraits/group_${options.version}/${i}.png`,
          alt: `person ${i}`,
          title: titles[options.version],
        });
      } else {
        images.push({
          src: `${IMAGES_API}/images/portraits/group_${options.version}/${i}.png`,
          alt: `person ${i}`,
          title: titles[options.version][i - 1],
        });
      }
    }
  } else if (options.version == 3 || options.version == 4) {
    for (let i = 1; i <= 10; i++) {
      if (options.version == 4) {
        images.push({
          src: `${IMAGES_API}/images/portraits/group_3/${i}.png`,
          alt: `person ${i}`,
          title: titles[options.version][i - 1],
          class: i <= 5 ? "man" : "woman",
        });
      } else {
        images.push({
          src: `${IMAGES_API}/images/portraits/group_${options.version}/${i}.png`,
          alt: `person ${i}`,
          title: titles[options.version][i - 1],
          class: i <= 5 ? "man" : "woman",
        });
      }
    }
  }

  let template = `<div class="age-cta-4-group-${options.version}">`;

  images.forEach((image, index) => {
    template += `
    <a
      href="${options.url}"
      target="${options.tab}"
      ${image.class ? `class="${image.class}"` : ""}
    >
      <img src="${image.src}" alt="${image.alt}" data-image>
      ${
        options.version == 2
          ? `<span class="desktop">${image.title.desktop[index]}</span><span class="mobile">${image.title.mobile[index]}</span>`
          : `      <span>${image.title}</span>`
      }
    </a>`;
  });

  return template;
};

const getSkinnyAgeButtonsTemplate = options => {
  const fourButtonsAges = ["Under 50", "50-70", "71-90", "Over 90"];
  const threeButtonsAges = ["Under 65", "65-80", "Over 80"];
  let template = '<div class="select-one-row select-one-row--small-mobile">';
  if (options?.count === "3") {
    for (const age of threeButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count} select-one-row__button--small-mobile"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor};"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  } else if (options?.count === "4") {
    for (const age of fourButtonsAges) {
      template += `
      <a
        href="${options?.url}"
        target="${options?.tab}"
        class="select-one-row__button select-one-row__button--${options?.count} select-one-row__button--small-mobile"
        style="background-color: ${options?.bgColor}; border-color: ${options?.bgColor};"
        onMouseOver="this.style.backgroundColor='${options?.hoverColor}'; this.style.borderColor='${options?.hoverColor}'"
        onMouseOut="this.style.backgroundColor='${options?.bgColor}'; this.style.borderColor='${options?.bgColor}'"
      >
        ${age}
      </a>
    `;
    }
  }

  template += "</div>";

  return template;
};

const getTeamMembersTemplate = () => {
  const template = `
    <div data-shortcode='teammembers' class="avatar__row">
      <div class="avatar__box">
        <img class="avatar__image" src="${editor}" alt="Editor icon">
        <div class="avatar__title">Linda</div>
        <div class="avatar__subtitle">Editor &amp; Content Writer</div>
    </div>
    <div class="avatar__box">
        <img class="avatar__image" src="${developer}" alt="Developer icon">
        <div class="avatar__title">Luke</div>
        <div class="avatar__subtitle">Chief Software Developer</div>
    </div>
    <div class="avatar__box">
        <img class="avatar__image" src="${association_member}" alt="Association member icon">
        <div class="avatar__title">Steve</div>
        <div class="avatar__subtitle">Senior Association Member</div>
    </div>
    <div class="avatar__box">
        <img class="avatar__image" src="${analyst}" alt="Analyst icon">
        <div class="avatar__title">Klara</div>
        <div class="avatar__subtitle">Business Analyst &amp; Admin Assistant</div>
    </div>
  </div>
  `;

  return template;
};

const getReviewRatingsTemplate = () => {
  const template = `
  <div class="bg-gray-1 pb-3 pt-6 px-8 relative">
  <div class="md:flex">
    <div class="md:w-7/12">
      <div class="text-left text-gray-3 uppercase text-12 font-bold">
        Product rating
      </div>
      <div>
        <div class="justify-start flex mt-6 w-full">
          <div class="w-9/12 relative">
            <div class="bg-gray-4 absolute w-full h-8 rounded-3xl"></div>
            <div class="bg-blue-1 absolute rounded-3xl h-8" style="width:90%">
              <div class="right-0 absolute mb-1 font-bold text-white text-12 mt-1 mr-5 text-bold">9</div>
            </div>
          </div>
          <div class="w-3/12 ml-4 text-gray-3">
            Real Savings
          </div>
        </div>

        <div class="justify-start flex mt-8 w-full">
          <div class="w-9/12 relative">
            <div class="bg-gray-4 absolute w-full h-8 rounded-3xl"></div>
            <div class="bg-blue-1 absolute rounded-3xl h-8" style="width:100%">
              <div class="right-0 absolute mb-1 font-bold text-white text-12 mt-1 mr-5 text-bold">10</div>
            </div>
          </div>
          <div class="w-3/12 ml-4 text-gray-3">
            Discounts
          </div>
        </div>

        <div class="justify-start flex mt-8 w-full">
          <div class="w-9/12 relative">
            <div class="bg-gray-4 absolute w-full h-8 rounded-3xl"></div>
            <div class="bg-blue-1 absolute rounded-3xl h-8" style="width:80%">
              <div class="right-0 absolute mb-1 font-bold text-white text-12 mt-1 mr-5 text-bold">8</div>
            </div>
          </div>
          <div class="w-3/12 ml-4 text-gray-3">
            Price
          </div>
        </div>

        <div class="justify-start flex mt-8 w-full">
          <div class="w-9/12 relative">
            <div class="bg-gray-4 absolute w-full h-8 rounded-3xl"></div>
            <div class="bg-blue-1 absolute rounded-3xl h-8" style="width:90%">
              <div class="right-0 absolute mb-1 font-bold text-white text-12 mt-1 mr-5 text-bold">9</div>
            </div>
          </div>
          <div class="w-3/12 ml-4 text-gray-3">
            Easy To Use
          </div>
        </div>

      </div>
    </div>

    <div class="md:w-4/12 mx-auto mt-8 md:mt-0">
      <div class="text-left text-gray-3 uppercase text-12 font-bold">
        Total rating
      </div>
      <div class="mt-6 review-block--total-block text-center bg-white rounded-xl">
        <div class="review-block--total-rating text-45 font-bold text-green-1">
          9.2
        </div>
        <div class="text-center flex justify-center mt-2">
          <img src="${fourStars}">
          <span class="ml-3 my-auto text-12 review-block--total-ratings">
            (14,027)
          </span>
        </div>
        <div class="py-10">
          <a href="#" class="text-20 px-8 py-4 rounded-xl font-bold text-white bg-green-2 no-underline text-white cta-button">
            CTA
          </a>
        </div>
      </div>
    </div>
  </div>
</div>
  `;

  return template;
};

const getSocialSharerTemplate = () => {
  const template = `
  <div data-shortcode='socialsharer' class="social-sharer__container">
    <a class="social-sharer__item social-sharer--fb-blue"
      data-share='facebook'
      href="https://www.facebook.com/sharer/sharer.php?u="
      target="_blank"
    >
      Share on Facebook
    </a>
    <a class="social-sharer__item social-sharer--tw-blue"
      data-share='twitter'
      href="https://twitter.com/intent/tweet?url="
        target="_blank"
      >
      Tweet
    </a>
    <a class="social-sharer__item social-sharer--mail-white"
      data-share='mail'
      href="_blank">
      Share via Mail
    </a>
  </div>
`;

  return template;
};

const getMultiStepQuizWithLoaderTemplate = (message = "") => {
  return `
          <div data-multi-step-quiz-data='${message}' class="quiz-with-loader" data-tiny-mce-component="multistepquiz">
            <img src="${quizImage}" class="quiz-edit">
          </div>
        `;
};

// Displays a loader that has 3 steps.
const getMultiStepQuizV2WithLoaderTemplate = () => {
  return `<img src="${quizImage}" class="v2-quiz-edit">`;
};

// Displays a loader that only says "please wait..."
const getMultiStepQuizV2Template = () => {
  return `<img src="${quizImage}" class="v2-quiz-edit" data-no-loader="true">`;
};

const getMultiStepQuizV3Template = () => {
  return `<img src="${quizImageV3}" class="v3-quiz-edit">`;
};

const getMultiStepQuizV4Template = () => {
  return `<img src="${quizImageV4}" class="v4-quiz-edit">`;
};

const getVueMultiStepQuizV4Template = (quizData = "") => {
  return `<multistep-quiz-v4 :quiz-data='${JSON.stringify(quizData)}' class="v4-quiz-edit">`;
};

const getContentSplitTemplate = () => {
  return `<hr class="content-split-edit" />`;
};

const getVueMultiStepQuizWithLoader = (quizData = "") => {
  // TODO: below string and quiz-data props attribute have to be with single quotes because otherwise JSON error in back API
  return `<multistep-quiz-with-loader :quiz-data='${JSON.stringify(quizData)}'></multistep-quiz-with-loader>`;
};

const getMultiStepQuizTemplate = (message = "") => {
  return `
          <div data-multi-step-quiz-data='${message}' data-tiny-mce-component="multistepquiz">
            <img src="${quizImage}" class="quiz-edit">
          </div>
        `;
};

const getVueMultiStepQuiz = (quizData = "") => {
  // TODO: below string and quiz-data props attribute have to be with single quotes because otherwise JSON error in back API
  return `<multistep-quiz :quiz-data='${JSON.stringify(quizData)}'></multistep-quiz>`;
};

/**
 * TODO: maybe change the component name (?)
 */
const getVueMultiStepQuizV2 = (quizData = "", displayLoader = true) => {
  return `<multistep-quiz-v2 :quiz-data='${JSON.stringify(
    quizData,
  )}' :display-loader=${displayLoader}></multistep-quiz-v2>`;
};

const getVueMultiStepQuizV3 = (quizData = "") => {
  return `<multistep-quiz-v3 :quiz-data='${JSON.stringify(quizData)}'></multistep-quiz-v3>`;
};

const getToastTemplate = () => {
  return `<img src="${toastImage}" class="toast-shortcode">`;
};
const getVueToastTemplate = (toastData = "", initialTime = "10000") => {
  return `<toast-component :toast-data='${JSON.stringify(toastData)}' :initial-time='${JSON.stringify(
    initialTime,
  )}'></toast-component>`;
};

const getLiveChatTemplate = () => {
  return `<img src="${livechatImage}" class="live-chat-shortcode">`;
};
const getLiveChatAutoTemplate = () => {
  return `<img src="${livechatImage}" class="live-chat-auto-shortcode">`;
};

const getVueLiveChatTemplate = (liveChatData = "") => {
  return `<live-chat :live-chat-data='${JSON.stringify(liveChatData)}'></live-chat>`;
};
const getVueLiveChatAutoTemplate = (liveChatData = "") => {
  return `<live-chat-auto :live-chat-data='${JSON.stringify(liveChatData)}'></live-chat-auto>`;
};

const getBottomBarTemplate = () => {
  return `<img src="${bottomBarImage}" class="bottom-bar-shortcode">`;
};
const getVueBottomBarTemplate = (bottomBarData = "") => {
  return `<bottom-bar :bottom-bar-data='${JSON.stringify(bottomBarData)}'></bottom-bar>`;
};

const getZipCodeTemplate = () => {
  return `<img src="${zipCodeImage}" class="zip-code-shortcode">`;
};

const getVueZipCodeTemplate = () => {
  return `<zip-code></zip-code>`;
};

const getAudioFormTemplate = () => {
  return `<img src="${audioFormImage}" class="audio-form-shortcode">`;
};

const getVueAudioFormTemplate = (audioFormData = "") => {
  return `<audio-form :audio-form-data='${JSON.stringify(audioFormData)}'></audio-form>`;
};

const getCommentsTemplate = (message = "") => {
  return `
            <div data-tiny-mce-component="landercomments" data-comments-widget-data='${message}'>
               <img class="comments-edit" src="${commentsWidgetImage}" >
            </div>
        `;
};

const getVueCommentsTemplate = commentsData => {
  // TODO: below string and quiz-data props attribute have to be with single quotes because otherwise JSON error in back API
  return `<comments-widget :comments-data='${JSON.stringify(commentsData)}'></comments-widget>`;
};

const getCountdownTemplate = data => {
  const timerColor = data?.timerColor;
  const date = data?.selectedDate;

  const timeRemaining = calculateTimeRemaining(date);

  const countDownTemplate = `
    <div class="countdown-shortcode" data-countdown-date="${date}">
      <div class="countdown">
        <p style="text-align: center;"><strong>ANNUAL ENROLLMENT ENDS IN:</strong></p>
        <hr class="countdown-divider" data-editable="false" data-trigger-edit="true">
        <div class="countdown-items" data-editable="false" data-trigger-edit="true">
            <div class="countdown-item" data-type="days">
                <div class="countdown-number" style="color: ${timerColor ? timerColor : "#1C42BE"}">
                  ${timeRemaining?.days}
                </div>
                <div class="countdown-label" style="color: ${timerColor ? timerColor : "#1C42BE"}">Days</div>
            </div>
            <div class="countdown-item" data-type="hours">
                <div class="countdown-number" style="color: ${timerColor ? timerColor : "#1C42BE"}">
                  ${timeRemaining?.hours}
                </div>
                <div class="countdown-label" style="color: ${timerColor ? timerColor : "#1C42BE"}">Hours</div>
            </div>
            <div class="countdown-item" data-type="minutes">
                <div class="countdown-number" style="color: ${timerColor ? timerColor : "#1C42BE"}">
                  ${timeRemaining?.minutes}
                </div>
                <div class="countdown-label" style="color: ${timerColor ? timerColor : "#1C42BE"}">Minutes</div>
            </div>
            <div class="countdown-item" data-type="seconds">
                <div class="countdown-number" style="color: ${timerColor ? timerColor : "#1C42BE"}">
                  ${timeRemaining?.seconds}
                </div>
                <div class="countdown-label" style="color: ${timerColor ? timerColor : "#1C42BE"}">Seconds</div>
            </div>
        </div>
      </div>
    </div>
  `;

  return countDownTemplate;
};

const getDripFormTemplate = options => {
  const template = `
  <div data-tiny-mce-component="dripform">
    <div class="drip__container">
      <div
        data-tiny-mce-component-data="title"
        class="drip__title"
      >
        ${options?.title}
      </div>
      <div
        class="drip__description"
        data-tiny-mce-component-data="subtitle"
        >
           ${options?.subtitle}
      </div>
      <div class="drip__group">
        <div class="drip__group__first">
          <input
            v-model="email"
            type="email"
            class="drip__input"
            placeholder="Email Address"
          >
        </div>
        <div class="drip__group__second">
          <div class="drip__button">
            <span
            data-tiny-mce-component-data="button-text"
            >
               ${options?.buttonText}
            </span>
            <img
              src="${IMAGES_API}/images/misc/arrow.svg"
              data-image
              alt="Arrow"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
`;

  return template;
};

const getVueDripTemplate = ({ title, subtitle, buttonText }) => {
  const template = `
    <drip-form
      title="${title}"
      subtitle="${subtitle}"
      button-text=${buttonText}
    >
    </drip-form>
    `;

  return template;
};

const getCurrentDateTemplate = (dateFormat = "MMMM", offset = 0) => {
  return `{{ shortcodes.date("${dateFormat}", "${offset || 0}") }}`;
};

const getCurrentLocationtemplate = (countryFormat = "country_long") => {
  return '{{ shortcodes.geo("' + countryFormat + '") }}';
};

const getTimerTemplate = quantity => {
  return `<span style="color: #ff0000;" class="timeout">${quantity}</span>`;
};

const getVueTimer = quantity => {
  return `<timer quantity="${quantity}"></timer>`;
};

const getCounterTemplate = quantity => {
  return `<span class="counter">{${quantity}}</span>`;
};

const getVueCounter = quantity => {
  return `<counter quantity="${quantity}"></counter>`;
};

const getHrBreakLineTemplate = (color, width) => {
  return `
             <hr style="width:${width}; color: ${color}; border-color: ${color};">
          `;
};

const videoTemplate = `
      <div class="tox-dropzone-container" id="video-file-container">
          <div class="tox-dropzone">
              <button type="button" class="tox-button tox-button--dropzone">
                  Click to browse
                  <input type="file" id="video-file-input" accept="video/*" class="tox-input--file hidden-file-input" ">
              </button>
          </div>
      </div>
`;

const getTextHighlightTemplate = type => {
  return `
  <div class="${type} info-box">

  </div>
  `;
};

const getDynamicHighlightBoxTemplate = color => {
  return `
  <div style="background-color: ${color}" class="dynamic-info-box">

  </div>
  `;
};

const getDynamicHighlightBoxWithBorderTemplate = options => {
  return `
    <div style="background-color: ${options?.bgColor}; border-color: ${options?.borderColor}" class="dynamic-info-box__with-border">

    </div>
  `;
};

const getContactFormTemplate = () => {
  return `
    <div
      data-tiny-mce-component="contactform"
      id="contact-form"
      data-contact="true"
      autocomplete="off"
      class="w-full md:w-7/12 my-6"
    >
      <label
        class="block text-black-4 font-semibold mb-1"
        for="name"
      >
        Full Name
      </label>
      <div class="flex">
        <img
          class="px-3 pr-3 rounded border border-gray-5 z-20 -mr-2"
          src="${IMAGES_API}/images/icons/input_user_icon.svg"
          data-image
          alt="User icon"
        />
        <input
          id="name"
          required
          class="border-l-0 z-10 appearance-none block w-full text-gray-700 rounded py-4 px-4 leading-tight focus:outline-none focus:bg-white border border-gray-6"
          type="text"
        />
      </div>
      <label
        class="block text-black-4 font-semibold mb-1 mt-3"
        for="email"
      >
        Email Address
      </label>
      <div class="flex">
        <img
          class="px-3 pr-3 rounded border border-gray-5 z-20 -mr-2"
          src="${IMAGES_API}/images/icons/input_user_email.svg"
          data-image
          alt="User email icon"
        />
        <input
          id="email"
          required
          type="email"
          class="border-l-0 z-10 appearance-none block w-full text-gray-700 rounded py-4 px-4 leading-tight focus:outline-none focus:bg-white border border-gray-6"
          type="email"
        />
      </div>
      <div class="w-full">
        <label
          class="block text-black-4 font-semibold font-mb-1 mt-3"
          for="message"
        >
          Your Message
        </label>
      </div>
      <textarea
        id="message"
        required
        rows="4"
        cols="50"
        class="appearance-none block w-full text-gray-700 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white border border-gray-6"
        type="text"
      ></textarea>
      <div>
        <button
          type="submit"
          value="Submit"
          id="contact-form-button"
          class="text-20 font-extrabold bg-green-3 border-b-4 border-green-4 hover:bg-green-4 items-center w-full justify-center mt-3 mx-auto px-1 py-4 md:p-4 rounded-md text-white font-bold mb-4"
        >
          Send Message
        </button>
      </div>
      <div id="success-message" class="text-green-3" style="display: none;">
        Your message has been successfully sent
      </div>
    </div>
  `;
};

const getMultiButtonCtaWithImagesBox = options => {
  let template = "";
  if (options?.count < 5) {
    template += `
      <a href="${options?.customUrl}" target="${options?.openLink}">
        <div
          class="multi-button-cta-with-images cursor-pointer bg-blue-7 mt-2 md:mt-4 md:mt-0 mx-auto md:mx-0 flex rounded-4.7px max-w-33 md:min-w-33">
          <div>
            <img class="rounded-multi-image-shortcode w-78 min-w-78 h-86"
              src="${options?.imgSrc}"
              alt="${options?.imgAlt}"
              />
          </div>
          <div class="cursor-pointer underline text-white leading-29 text-21 font-bold font-arial pl-6 my-auto">
            ${options?.boxInput}
          </div>
          <div class="my-auto px-6 ml-auto">
            <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.2246 12.1502L0.238519 23.8312L0.238519 0.469171L11.2246 12.1502Z" fill="white" />
            </svg>
          </div>
        </div>
      </a>
    `;
  } else if (options?.count === 5) {
    template += `
        <a class="w-full" href="${options?.customUrl}" target="${options?.openLink}">
          <div
            class="multi-button-cta-with-images cursor-pointer bg-blue-7 mt-2 md:mt-4 md:mt-0 mx-auto md:mx-auto flex rounded-4.7px max-w-33 md:max-w-68">
            <div>
              <img class="rounded-multi-image-shortcode w-78 min-w-78 h-86"
                src="${options?.imgSrc}"
                alt="${options?.imgAlt}"
                />
            </div>
            <div class="cursor-pointer underline text-white leading-29 text-21 font-bold font-arial pl-6 my-auto">
              ${options?.boxInput}
            </div>
            <div class="my-auto px-6 ml-auto">
              <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.2246 12.1502L0.238519 23.8312L0.238519 0.469171L11.2246 12.1502Z" fill="white" />
              </svg>
            </div>
          </div>
        </a>
    `;
  }

  return template;
};

const getMultiButtonCtaWithImages = boxesInfo => {
  let template = `
    <div class="mx-auto px-6 md:px-0">
      <div class="md:flex md:space-x-8 mt-2 md:mt-4 md:justify-center">
    `;

  for (let x = 1; x <= boxesInfo?.quantity; x++) {
    const boxOptions = {
      count: x,
      customUrl: boxesInfo?.data?.customUrl,
      openLink: boxesInfo?.data?.openLink,
      imgAlt: boxesInfo?.data["imgAlt" + x],
      boxInput: boxesInfo?.data["boxInput" + x],
      imgSrc: boxesInfo?.data["imgSrc" + x]?.value,
    };
    if (x === 3) {
      template += `
        </div>
        <div class="md:flex md:space-x-8 mt-2 md:mt-4 justify-center">
      `;
    }
    if (x === 5) {
      template += `
        </div>
        <div class="md:flex md:space-x-8 mt-2 md:mt-4 justify-center">
      `;
    }
    template += getMultiButtonCtaWithImagesBox(boxOptions);
  }
  template += `
    </div>
  </div>
   `;

  return template;
};

const getVueContactTemplate = () => {
  const template = "<contact-form></contact-form>";

  return template;
};

const getUsStatesShortcodeTemplate = (states, options) => {
  let template = '<div class="md:flex md:flex-wrap">';
  states.forEach(state => {
    template += `
    <div class="w-full md:w-1/3 p-3">
      <a
        data-shortcode='buttons'
        class="button button--padding-fix button--${options.type} button-state"
        href="${options.url}"
        target="${options.tab}"
        >
        <span>${state.name}</span>
      </a>
    </div>`;
  });
  template += "</div>";

  return template;
};

const getCheckoutFormTemplate = () => {
  return `
    <div data-tiny-mce-component="checkoutform">
       <div class="max-w-67 mx-auto border-0 md:border-2 border-gray-4 rounded-xl py-12 md:py-16 px-6 md:px-28 font-robo">
            <form>
          <div class="text-38 text-center mb-12 md:mb-16 font-bold hidden md:block">
            Secure Order Form
          </div>
          <div>
            <div>
                <div class="relative pt-0 text-text-black">
                    <div class="text-24 text-blue-8 font-medium mb-12 md:mb-16">
                        Personal Information
                    </div>
                    <div>
                        <div>
                            <div class="md:flex md:-mx-4">
                                <div class="md:mx-4 w-full md:w-2/4 mb-10">
                                    <div class="">
                                        <label for="firstName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                            First Name*
                                        </label>
                                        <div class="flex flex-grow justify-center group">
                                            <input type="text" placeholder="" id="firstName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                                    </div>
                                    </div>
                                 </div>
                                <div class="md:mx-4 w-full md:w-2/4 mb-10">
                                   <div class="">
                                      <label for="lastName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                          Last Name*
                                      </label>
                                      <div class="flex flex-grow justify-center group">
                                        <input type="text" placeholder="" id="lastName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                                    </div>
                                   </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="">
                    <div class="text-24 text-blue-8 font-medium mb-12 md:mb-16">
                        Billing Address
                    </div>
                    <div class="mb-8">
                          <div class="">
                              <label for="cardHolder" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                  Address*
                              </label>
                              <div class="flex flex-grow justify-center group">
                                  <input type="text" placeholder="" id="cardHolder" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 h-20">
                              </div>
                          </div>
                    </div>
                    <div class="md:flex md:-mx-4">
                        <div class="md:mx-4 w-full md:w-2/4 mb-10">
                            <div class="">
                                <label for="firstName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                    City*
                                </label>
                                <div class="flex flex-grow justify-center group">
                                    <input type="text" placeholder="" id="firstName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                            </div>
                            </div>
                         </div>
                        <div class="md:mx-4 w-full md:w-2/4 mb-10">
                           <div class="">
                              <label for="lastName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                  Country*
                              </label>
                              <div class="flex flex-grow justify-center group">
                                <input type="text" placeholder="" id="lastName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                            </div>
                           </div>
                        </div>
                    </div>
                    <div class="md:flex md:-mx-4">
                        <div class="md:mx-4 w-full md:w-2/4 mb-10">
                            <div class="">
                                <label for="firstName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                    State*
                                </label>
                                <div class="flex flex-grow justify-center group">
                                    <input type="text" placeholder="" id="firstName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                            </div>
                            </div>
                         </div>
                        <div class="md:mx-4 w-full md:w-2/4 mb-10">
                           <div class="">
                              <label for="lastName" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                  Postal Code*
                              </label>
                              <div class="flex flex-grow justify-center group">
                                <input type="text" placeholder="" id="lastName" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 capitalize h-20">
                            </div>
                           </div>
                        </div>
                    </div>
                </div>
          </div>
          <div class="relative">
              <div class="text-24 text-blue-8 font-medium mt-2 md:mt-6 mb-12 md:mb-16 ">
                    Payment Information
              </div>
              <div>
                  <div>
                      <div>
                          <div>
                              <div class="mb-8">
                                  <div class="">
                                      <label for="cardHolder" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                          Card Holder Name*
                                      </label>
                                      <div class="flex flex-grow justify-center group">
                                          <input type="text" placeholder="" id="cardHolder" inputmode="text" class="focus:outline-none px-5 w-full border-black-703 border-2 h-20">
                                      </div>
                                  </div>
                              </div>
                              <div class="mb-8 mt-6 md:mt-0">
                                  <div class="relative">
                                      <label for="cardNumber" class="flex items-center text-black-702 text-16 leading-25 font-bold">
                                          Card Number*
                                          <img src="${cards}" alt="cards" class="ml-2 mb-2 h-12">
                                      </label>
                                      <div class="relative">
                                          <input type="text" value="" id="cardNumber" inputmode="tel" class="focus:outline-none px-5 w-full border-black-703 border-2 h-20">
                                      </div>
                                  </div>
                                  <div class="md:flex mt-8">
                                      <div class="md:w-1/2 mr-64 md:mr-8 mb-10 md:mb-0">
                                        <div>
                                            <div>
                                                <label for="card" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                                    Expiry Date*
                                                </label>
                                                <div class="flex justify-between items-baseline">
                                                    <div class="custom-width-50">
                                                        <select id="" class="checkout-select">
                                                            <option disabled="disabled">Month</option>
                                                            <option value="01">01</option>
                                                            <option value="02">02</option>
                                                            <option value="03"> 03</option>
                                                            <option value="04">04</option>
                                                        </select>
                                                    </div>
                                                    <span class="text-20">/</span>
                                                    <div class="custom-width-50">
                                                        <select id="" class="checkout-select">
                                                            <option disabled="disabled" hidden="hidden">Year</option>
                                                            <option value="2021">2021</option>
                                                            <option value="2022">2022</option>
                                                            <option value="2023">2023</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                      <div class="md:w-1/2 mb-6 md:mb-0 flex">
                                        <div class="relative flex-1">
                                            <div class="">
                                                <label for="cardSecurityCode" class="block mb-2 text-black-702 text-16 leading-25 font-bold">
                                                    CVV/CVC*
                                                </label>
                                            <div class="flex flex-grow justify-center group">
                                                <input type="text" placeholder="" id="cardSecurityCode" maxlength="4" inputmode="tel" class="focus:outline-none px-5 w-full border-black-703 border-2 h-20">
                                            </div>
                                        </div>
                                    </div>
                                    <div class=" min-w-64px">
                                        <img src="${cvvIcon}" alt="cvv" class="ml-2 mb-2 mt-16">
                                    </div>
                                </div>
                                  </div>
                      </div>
                  </div>
              </div>
          </div>
          <div class="pt-4 mx-auto text-left">
              <div class="pb-3 text-15 font-semibold text-black ">
                  Order summary
              </div>
              <div class="flex text-15 pb-2 text-black-704">
                  <div>Lifetime Membership</div>
                  <div class="ml-auto line-through">$199</div>
              </div>
              <div class="flex text-15 pb-3 text-black-704"><div>
                Discount -80%
              </div>
              <div class="ml-auto text-red-700 font-semibold">-$160</div>
          </div>
          <div class="flex pb-8 text-bold">
              <div class="font-bold text-16">Total</div>
              <div class="ml-auto text-16 font-bold">USD $39</div>
          </div>
          <div class="text-15 text-black-704">
                This is a one-time purchase.  No subscriptions or monthly charges. No hidden fees.
          </div>
          <div>
              <button type="button" class="text-24 leading-46 block font-bold text-white py-6 px:8 md:px-16 mx-auto rounded-lg bg-green-10">
                      Submit My Order
              </button>
          </div>
       </div>
    <div class="my-12 flex justify-center items-center">
          <img src="${guarantee}" alt="" class="mr-8 max-w-xl">
              <div class="leading-26 text-14 font-open text-black-705">
                  This is a one-time payment, does not auto-renew.
                  No subscriptions, no hidden fees.
                  You’re 100% backed by our 30-Day Money-Back Guarantee.
              </div>
          </div>
          <div class="px-6 mx-auto max-w-3xl">
          <div class="text-3xl text-center mb-2">
              <span class="font-bold text-green-700">
                  <img class="inline" src="${greenLock}" alt="green lock icon">
                SSL
              </span>
              SECURED PAYMENT
          </div>
          <div class="text-10 md:text-12 text-center">
              Your information is protected by 256-bit SSL encryption
          </div>
      </div>
      </div>
      </form>
       </div>
    </div>
  `;
};

const getVueCheckoutFormTemplate = () => {
  return `
    <checkout-form></checkout-form>
  `;
};

const getEmailFormTemplate = () => {
  return `
    <div data-tiny-mce-component='emailform'>
        <label
          for="email_address"
          class="text-black-4 text-18 leading-28"
          data-tiny-mce-component-data="label"
        >
        Enter Email Address:
        </label>
        <div class="">
            <input id="email_address" type="text" class="border border-gray-13 rounded-md p-3.5 leading-26 min-w-26">
        </div>
        <div class="mt-8">
          <div
              class="text-10 leading-14 inline"
              data-tiny-mce-component-data="text"
          >
              By submitting your email address, you consent to us keeping you informed about updates to our website and about other products and services that we think might interest you. You can unsubscribe at any time. Please read our
          </div>&nbsp;
          <a href=""
             class="text-blue-8 text-10 leading-14"
             target="_self"
             data-tiny-mce-component-data="link"
             >
             Privacy Statement Terms & Conditions
          </a>
        </div>
        <div class="mt-4">
            <button class="text-center text-26 font-bold text-white rounded-6 py-8 px-24 leading-none shadow-gray-bottom bg-gradient-to-b from-green-9 to-green-11 md:hover:bg-gradient-to-t md:hover:from-green-11 md:hover:to-green-9"
                    type="submit"
                    data-tiny-mce-component-data="buttonText"
            >
                Continue »
            </button>
        </div>
    </div>
  `;
};

const getVueEmailFormTemplate = ({ label, text, buttonText, linkText, linkHref, linkTarget }) => {
  const template = `
    <email-form
      label="${label}"
      text="${text}"
      button-text=${buttonText}
      link-text="${linkText}"
      link-href="${linkHref}"
      link-target="${linkTarget}"
    >
    </email-form>
    `;

  return template;
};

const abSplitTest = () => {
  return `
    <div data-split-test="" class="py-4">
        <hr>
    </div>
  `;
};

const getAnimatedButtonTemplate = options => {
  return `<a
          onMouseOver="this.style.backgroundColor='${options?.hoverColor}'"
          onMouseOut="this.style.backgroundColor='${options?.bgColor}'"
          style="background-color: ${options?.bgColor}"
          class="animated-button-shortcode"
          href="${options?.url}"
          ${options?.tab ? `target="${options.tab}"` : ""}
          data-btn-type="${options?.type}"
        >
        <div class="content-wrapper">
          ${
            options?.type === "call"
              ? ` <div>
                <img class="animated-button-shortcode__phone" src="${IMAGES_API}/images/phone.png" alt="Phone"/>
              </div>`
              : ``
          }
          <div class="animated-button-shortcode__text">
            ${options?.text}
          </div>
        </div>
        <img class="animated-button-shortcode__tap" src="${IMAGES_API}/images/tap.gif" alt="Tap"/>
  </a>`;
};

const getCarrierNamesOtcTemplate = () => {
  return `<span class="carrier-names" type="otc">{Carrier Names Otc}</span>`;
};
const getCarrierNamesPartBTemplate = () => {
  return `<span class="carrier-names" type="partb">{Carrier Names Part B}</span>`;
};

const getVueCarrierNames = type => {
  return `<carrier-names type="${type}"></carrier-names>`;
};

export {
  getCTATemplate,
  getShortCTATemplate,
  getMultipleCTATemplate,
  getQuoteBoxTemplate,
  getQuoteTemplate,
  getSelectAgeTemplate,
  getSelectAgeIconsTemplate,
  getMileageTemplate,
  getTeamMembersTemplate,
  getReviewRatingsTemplate,
  getCurrentDateTemplate,
  getCurrentLocationtemplate,
  getDripFormTemplate,
  getVueDripTemplate,
  getSocialSharerTemplate,
  getCommentsTemplate,
  getVueCommentsTemplate,
  getCountdownTemplate,
  getContactFormTemplate,
  getVueContactTemplate,
  getTextHighlightTemplate,
  getCheckoutPlanTemplate,
  getUsStatesShortcodeTemplate,
  getCTAIndyTemplate,
  getCTAOuterTemplate,
  getGifAgeTemplate,
  getHrBreakLineTemplate,
  getMultiButtonCta,
  getMultiButtonCtaWithImages,
  getWhiteMultiButtonCta,
  getCTATemplateWidthAd,
  getCheckoutFormTemplate,
  getVueCheckoutFormTemplate,
  getEmailFormTemplate,
  getVueEmailFormTemplate,
  getSelectAgeInOneRowTemplate,
  getAgeCTA3Template,
  getSkinnyAgeButtonsTemplate,
  getMultiStepQuizTemplate,
  getMultiStepQuizWithLoaderTemplate,
  getMultiStepQuizV2WithLoaderTemplate,
  getMultiStepQuizV2Template,
  getVueMultiStepQuiz,
  getVueMultiStepQuizV2,
  getVueMultiStepQuizWithLoader,
  getDynamicHighlightBoxTemplate,
  getDynamicHighlightBoxWithBorderTemplate,
  getCtaButtonNumberTemplate,
  getCTAPhoneTemplate,
  getMultiButtonCtaWithSeparateLinks,
  getDynamicColorMultiButtonCta,
  getShortcodeHTMLTemplate,
  getTimerTemplate,
  getVueTimer,
  abSplitTest,
  getContentSplitTemplate,
  getToastTemplate,
  getVueToastTemplate,
  getLiveChatTemplate,
  getVueLiveChatTemplate,
  getBottomBarTemplate,
  getVueBottomBarTemplate,
  getZipCodeTemplate,
  getVueZipCodeTemplate,
  getAudioFormTemplate,
  getVueAudioFormTemplate,
  getAnimatedButtonTemplate,
  getVueCounter,
  getCounterTemplate,
  getCarrierNamesOtcTemplate,
  getCarrierNamesPartBTemplate,
  getVueCarrierNames,
  getMultiStepQuizV3Template,
  getVueMultiStepQuizV3,
  getMultiStepQuizV4Template,
  getVueMultiStepQuizV4Template,
  getMultiButtonCtaV3Template,
  getMoneyCTATemplate,
  getMultiButtonCtaV4Template,
  getButtonsWithTextBoxTemplate,
  getButtonsWithTextBoxVueTemplate,
  getLiveChatAutoTemplate,
  getVueLiveChatAutoTemplate,
  getAgeCTA4Template,
  videoTemplate,
  getLiveChatCTATemplate,
};
